import apiClient from '../utils/apiClient';
import { API_ENDPOINTS } from '../config/api.config';

/**
 * Research Service
 * Handles all research and search-related API calls
 */
class ResearchService {
  // ==================== SEMANTIC SEARCH ENDPOINTS ====================

  /**
   * Semantic search with tag matching and cosine similarity
   * @param {Object} searchParams - { query, dataset, top_k }
   * @returns {Promise} API response
   */
  async semanticSearch(searchParams, options = {}) {
    const response = await apiClient.post(
      '/research/semantic-search/',
      searchParams,
      false,
      options
    );
    return response;
  }

  /**
   * Search data view records across one or all datasets.
   * @param {Object} searchParams - { query, dataset, page, per_page }
   * @returns {Promise} API response
   */
  async searchDataView(searchParams) {
    const response = await apiClient.post(
      API_ENDPOINTS.RESEARCH.DATA_VIEW_SEARCH,
      searchParams
    );
    return response;
  }

  /**
   * Download search results as CSV
   * @param {Object} downloadParams - { results, dataset, format }
   * @returns {Promise} File download
   */
  async downloadSearchResults(downloadParams) {
    try {
      const response = await apiClient.request(
        `${process.env.REACT_APP_API_URL || 'http://localhost:8000'}/api/v1/research/download-results/`,
        {
        method: 'POST',
        body: JSON.stringify(downloadParams),
      });

      if (!response.ok) {
        throw new Error('Download failed');
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `${downloadParams.dataset}_results.csv`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      return { status: 'success' };
    } catch (error) {
      console.error('Download error:', error);
      throw error;
    }
  }

  /**
   * Download single result as transposed CSV
   * @param {Object} result - Result object
   * @param {String} dataset - Dataset name
   * @returns {Promise} File download
   */
  async downloadSingleResult(result, dataset) {
    try {
      const response = await apiClient.request(
        `${process.env.REACT_APP_API_URL || 'http://localhost:8000'}/api/v1/research/download-single/`,
        {
        method: 'POST',
        body: JSON.stringify({ result, dataset }),
      });

      if (!response.ok) {
        throw new Error('Download failed');
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `${dataset}_record.csv`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      return { status: 'success' };
    } catch (error) {
      console.error('Download error:', error);
      throw error;
    }
  }

  // ==================== SEARCH ENDPOINTS ====================

  /**
   * Global search across all datasets
   * @param {Object} searchParams - Search parameters
   * @returns {Promise} API response
   */
  async globalSearch(searchParams) {
    const response = await apiClient.post(
      API_ENDPOINTS.RESEARCH.GLOBAL_SEARCH,
      searchParams
    );
    return response;
  }

  // ==================== EXPORT ====================

  /**
   * Create an export job
   * @param {Object} exportData - Export configuration
   * @returns {Promise} API response
   */
  async createExport(exportData) {
    const response = await apiClient.post(
      API_ENDPOINTS.RESEARCH.EXPORT,
      exportData
    );
    return response;
  }

  /**
   * Get export job status
   * @param {string} jobId - Export job ID
   * @returns {Promise} API response
   */
  async getExportStatus(jobId) {
    const response = await apiClient.get(
      API_ENDPOINTS.RESEARCH.EXPORT_DETAIL(jobId)
    );
    return response;
  }

  /**
   * Download export file
   * @param {string} jobId - Export job ID
   * @param {string} filename - Download filename
   * @returns {Promise} File download
   */
  async downloadExport(jobId, filename = 'export.csv') {
    await apiClient.downloadFile(
      API_ENDPOINTS.RESEARCH.EXPORT_DOWNLOAD(jobId),
      filename
    );
  }

  /**
   * Get all export jobs
   * @param {Object} params - Query parameters
   * @returns {Promise} API response
   */
  async getExports(params = {}) {
    const response = await apiClient.get(
      API_ENDPOINTS.RESEARCH.EXPORT,
      params
    );
    return response;
  }

}

const researchService = new ResearchService();

export default researchService;
